<script setup>
import { computed, ref, watch } from 'vue';
import {
  useFunctionGetter,
  useStoreGetters,
} from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { fetchTrayOrders } from '../../../api/tray';
import TrayOrderItem from './TrayOrderItem.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
});

const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;
const contact = useFunctionGetter('contacts/getContact', props.contactId);
const { t } = useI18n();

const hasSearchableInfo = computed(
  () => !!contact.value?.email || !!contact.value?.phone_number
);

const orders = ref([]);
const loading = ref(true);
const hasError = ref(false);
const errorMessage = computed(() => t('CONVERSATION_SIDEBAR.TRAY.ERROR'));

const fetchOrders = async () => {
  try {
    loading.value = true;
    orders.value = [];
    hasError.value = false;

    const response = await fetchTrayOrders(accountId.value, props.contactId);
    orders.value = response.data.orders;
  } catch {
    hasError.value = true;
  } finally {
    loading.value = false;
  }
};

watch(
  () => [props.contactId, hasSearchableInfo.value],
  () => {
    if (hasSearchableInfo.value) {
      fetchOrders();
    } else {
      loading.value = false;
      orders.value = [];
      hasError.value = false;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="px-4 py-2 text-n-slate-12">
    <div v-if="!hasSearchableInfo" class="text-center text-n-slate-12">
      {{ $t('CONVERSATION_SIDEBAR.TRAY.NO_ORDERS') }}
    </div>
    <div v-else-if="loading" class="flex items-center justify-center p-4">
      <Spinner size="32" class="text-n-brand" />
    </div>
    <div v-else-if="hasError" class="text-center text-n-ruby-12">
      {{ errorMessage }}
    </div>
    <div v-else-if="!orders.length" class="text-center text-n-slate-12">
      {{ $t('CONVERSATION_SIDEBAR.TRAY.NO_ORDERS') }}
    </div>
    <div v-else>
      <TrayOrderItem v-for="order in orders" :key="order.id" :order="order" />
    </div>
  </div>
</template>
